import React, { useEffect, useState } from 'react';
import { Input, Modal, TextArea, useForm } from 'linkwithus';
import './styles.scss';
import { motion } from 'framer-motion';
import config from 'config';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import SiteButton from 'components/SiteButton';
import FileUploader from 'components/FileUploader';
import { PlaceModalMode, IPlaceForm, IPlaceModalProps } from './interface';
import { contentSettingsModalMotion } from './animation';

function PlaceModal({
    modalVisibility,
    close,
    item,
    mode = PlaceModalMode.Edit,
    updateList,
}: IPlaceModalProps) {
    const { t } = useTranslation();
    const [imageUrl, setImageUrl] = useState<string>();
    const [imageVisibility, setImageVisibility] = useState(false);

    const { register, form, submit, updateForm, setForm } = useForm<IPlaceForm>(
        {
            url: `/places/${
                mode === PlaceModalMode.Edit && item ? item.Id : ''
            }`,
            type: mode === PlaceModalMode.Edit ? 'PUT' : 'POST',
            onSuccess: () => {
                notification.success({
                    message: t('general.SUCCESS'),
                    description: t(
                        `contentSettingsPage.${
                            mode === PlaceModalMode.Edit
                                ? 'PLACE_EDITED_WITH_SUCCESS'
                                : 'PLACE_CREATED_WITH_SUCCESS'
                        }`,
                    ),
                });
                updateList();
                close();
            },
            onError: (data, error: any) => {
                const errorCode = Number(error.errorCode) || 0;
                notification.error({
                    message: t(`general.ERROR`),
                    description: t(`errors.${errorCode}`),
                });
                updateList();
                close();
            },
            resolveData(rawForm) {
                const data = new FormData();
                Object.keys(rawForm).forEach(key => {
                    if (key === 'photo') {
                        rawForm.photo.forEach((f: any) => {
                            data.append('photo', f as any);
                        });
                    } else
                        data.append(
                            key,
                            rawForm[key as keyof IPlaceForm] as any,
                        );
                });
                return data;
            },
        },
    );

    useEffect(() => {
        if (modalVisibility && mode === PlaceModalMode.Edit && !item) {
            close();
        }
        if (modalVisibility && mode === PlaceModalMode.Edit && item) {
            const currentDate = new Date();
            updateForm('name', item.Placelanguages[0].Name);
            updateForm('description', item.Placelanguages[0].Description);
            setImageUrl(
                `${config.API_URL}/places/images/${item.Id}?date=${currentDate}`,
            );
            setTimeout(() => {
                setImageVisibility(true);
            }, 100);
        }
        if (!modalVisibility) {
            setForm({});
            setImageVisibility(false);
        }
        if (modalVisibility && mode === PlaceModalMode.Create) {
            setImageUrl(undefined);
            setTimeout(() => {
                setImageVisibility(true);
            }, 100);
        }
    }, [modalVisibility]);

    return (
        <Modal
            visible={modalVisibility}
            close={close}
            className='place-modal'
            width={700}
        >
            <motion.div
                {...contentSettingsModalMotion}
                className='place-modal__content'
            >
                <p className='place-modal__content__title'>
                    {t(
                        `contentSettingsPage.${
                            mode === PlaceModalMode.Edit
                                ? 'EDIT_PLACE'
                                : 'CREATE_PLACE'
                        }`,
                    )}
                </p>
                <div className='place-modal__content__body'>
                    <div className='place-modal__content__body__img-container'>
                        {imageVisibility && (
                            <FileUploader
                                {...register('photo')}
                                initial={imageUrl}
                                className='rectangular'
                            />
                        )}
                    </div>
                    <p className='place-modal__content__body__label'>
                        {t('form.NAME')}
                    </p>
                    <Input
                        {...register('name', {
                            required: true,
                            resolveError: () =>
                                `${t('form.NAME')} ${t('form.REQUIRED_FIELD')}`,
                        })}
                        className='place-modal__content__body__field'
                    />
                    <p className='place-modal__content__body__label'>
                        {t('form.DESCRIPTION')}
                    </p>
                    <TextArea
                        {...register('description', {
                            required: true,
                            resolveError: () =>
                                `${t('form.DESCRIPTION')} ${t(
                                    'form.REQUIRED',
                                )}`,
                        })}
                        autoSize
                        onPressEnter={() => {
                            //
                        }}
                        className='place-modal__content__body__field president-text-area'
                    />
                </div>
                <div className='place-modal__content__footer'>
                    <SiteButton
                        text={t('general.CANCEL')}
                        type='primary'
                        className='place-modal__content__footer__button grey'
                        onClick={close}
                    />
                    <SiteButton
                        text={t('form.SAVE_CHANGES')}
                        type='primary'
                        className='place-modal__content__footer__button'
                        onClick={submit}
                        disabled={
                            mode === PlaceModalMode.Create &&
                            (!form.name ||
                                !form.description ||
                                !form.photo ||
                                (form.photo &&
                                    Array.isArray(form.photo) &&
                                    !(form.photo.length > 0)))
                        }
                    />
                </div>
            </motion.div>
        </Modal>
    );
}

export default PlaceModal;
